import { getProductKitItemsData } from '../productKitItems/mockData'
import {
  getProductGroupListData,
  _getPRGSelectItemDataById,
} from '../productGroups/mockData'
import { getListDataFunctionFactory } from '../../modules/getListDataFunctionFactory'
import type { TEntityId, IEntitySelectItem } from '~/types/models'
import type {
  TProduct,
  IKitProduct,
  IItemProduct,
  IKitProductOverview,
  IProductKitItem,
} from '~/types/product/models'
import type { ISupplier } from '~/types/supplier/models'
import type { ISupplierProduct } from '~/types/supplierProduct/models'
import { ProductLevelIds } from '~/services/selectLists/productLevels'
import { getSupplierProductListData } from '~/api/msw/api/supplierProducts/mockData'

const productListData = undefined as unknown as (IItemProduct | IKitProduct)[]

export const getProductListData = getListDataFunctionFactory<
  (IItemProduct | IKitProduct)[]
>({
  listData: productListData,
  storageKey: 'products',
  getStartingListData: getStartingProductListData,
})

export async function _getPRDataById(
  id: TEntityId,
  list: TProduct[],
  isInitializingKitData: boolean
): Promise<TProduct | undefined> {
  const fn = '[api/products/mockData] _getPRDataById |'
  if (!list) {
    throw new Error(`_getPRDataById called with undefined/null list`)
  }
  const data = list.find((product) => product.id === id)
  if (isInitializingKitData) {
    console.debug(
      `${fn} Iinitializing kit data. NOT getting kit items to get kit items count.`
    )
    return data
  }
  return data
}

export function _getPRSelectItemDataById(
  id: TEntityId,
  list: TProduct[]
): IEntitySelectItem | undefined {
  if (!list) {
    throw new Error(`_getPRSelectItemDataById called with undefined/null list`)
  }
  const product = list.find((product) => product.id === id)
  if (!product) {
    return undefined
  }
  return { id: product.id, label: product.descr }
}

export async function getProductData(
  id: string,
  isInitializingKitData: boolean = false
) {
  const listData: (IItemProduct | IKitProduct)[] = await getProductListData()
  return _getPRDataById(id, listData, isInitializingKitData)
}

export async function getProductSelectItemDataById(id: string) {
  const listData = await getProductListData()
  return _getPRSelectItemDataById(id, listData)
}

export async function getKitProductListData() {
  return await getProductListDataForLevelId(ProductLevelIds.KIT)
}

export async function getItemProductListData() {
  return await getProductListDataForLevelId(ProductLevelIds.ITEM)
}

export async function getProductListDataForLevelId(id: number) {
  const listData: (IItemProduct | IKitProduct)[] = await getProductListData()
  return listData.find((product) => product.level.id === id)
}

export async function getProductOverviewData(
  id: string
): Promise<IKitProductOverview | undefined> {
  const productData = await getProductData(id)
  if (!productData) {
    return undefined
  }
  const productKitItemsData = (await getProductKitItemsData(id)) || []
  const overviewData = {
    product: productData as IKitProduct,
    kitItems: productKitItemsData as IProductKitItem[],
  }
  console.debug(overviewData)
  return overviewData
}

export async function getSuppliersForProductData(
  id: string,
  wellFormedSupplierProductsOnly: boolean = false
): Promise<ISupplier[] | undefined> {
  const supplierProductListData: ISupplierProduct[] =
    await getSupplierProductListData()
  return supplierProductListData
    .filter(
      (supplierProduct) =>
        supplierProduct.product.id === id &&
        (!wellFormedSupplierProductsOnly ||
          (wellFormedSupplierProductsOnly &&
            supplierProduct.numItems &&
            supplierProduct.numItems > 0))
    )
    .map((supplierProduct) => supplierProduct.supplier)
}

export async function getStartingProductListData(): Promise<
  (IItemProduct | IKitProduct)[]
> {
  const prgList = await getProductGroupListData()
  return [
    {
      id: 'XXPR0003',
      hrid: 'PR0003',
      productGroup: _getPRGSelectItemDataById('XXPRG0009', prgList),
      descr: 'License (SDWAN)',
      type: {
        id: 1,
        label: 'Service',
      },
      level: {
        id: 2,
        label: 'Item',
      },
      keyIdentifierFieldName: 'circuitId',
      numKitItems: 0,
      numSuppliers: 2,
      numOpportunities: 150,
      numOrders: 143,
      numServices: 143,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0005',
      hrid: 'PR0005',
      productGroup: _getPRGSelectItemDataById('XXPRG0004', prgList),
      descr: 'Edge Router',
      type: {
        id: 2,
        label: 'Hardware',
      },
      level: {
        id: 2,
        label: 'Item',
      },
      keyIdentifierFieldName: 'Model',
      numKitItems: 0,
      numSuppliers: 3,
      numOpportunities: 10,
      numOrders: 8,
      numServices: 8,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0006',
      hrid: 'PR0006',
      productGroup: _getPRGSelectItemDataById('XXPRG0005', prgList),
      descr: 'POTS',
      type: {
        id: 1,
        label: 'Service',
      },
      level: {
        id: 2,
        label: 'Item',
      },
      fieldSet: {
        id: 'XXFS0010',
        label: 'POTS Line',
      },
      // ⚠ DELIBERATELY EMPTY, and the fixture for the key-identifier feature.
      // `products.key_identifier_field_name` is NULL for EVERY product on the
      // real API -- there is no input-spec field that can set it -- so a mock
      // that names the field here is wider than the server and hides the fact
      // that the product route is dead. With it empty, the POTS lines' key
      // identifiers can only come from the fieldSet this product points at
      // (POTS Line -> `phoneNumber`), which is the rule the app now follows.
      // See cynqueModules/fieldSetKeyIdentifier.ts and
      // e2e/keyIdentifierFromFieldSet.spec.ts.
      keyIdentifierFieldName: '',
      numKitItems: 0,
      numSuppliers: 1,
      numOpportunities: 234,
      numOrders: 45,
      numServices: 45,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0007',
      hrid: 'PR0007',
      productGroup: _getPRGSelectItemDataById('XXPRG0004', prgList),
      descr: 'Managed Router',
      type: {
        id: 2,
        label: 'Hardware',
      },
      level: {
        id: 2,
        label: 'Item',
      },
      keyIdentifierFieldName: 'Model',
      numKitItems: 0,
      numSuppliers: 4,
      numOpportunities: 22,
      numOrders: 18,
      numServices: 18,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0008',
      hrid: 'PR0008',
      productGroup: _getPRGSelectItemDataById('XXPRG0007', prgList),
      descr: 'VOIP Phone',
      type: {
        id: 2,
        label: 'Hardware',
      },
      level: {
        id: 2,
        label: 'Item',
      },
      keyIdentifierFieldName: 'Model',
      numKitItems: 0,
      numSuppliers: 2,
      numOpportunities: 14,
      numOrders: 10,
      numServices: 10,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0009',
      hrid: 'PR0009',
      productGroup: _getPRGSelectItemDataById('XXPRG0003', prgList),
      descr: 'Consulting Services',
      type: {
        id: 1,
        label: 'Service',
      },
      level: {
        id: 2,
        label: 'Item',
      },
      keyIdentifierFieldName: '',
      numKitItems: 0,
      numSuppliers: 1,
      numOpportunities: 490,
      numOrders: 425,
      numServices: 425,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0010',
      hrid: 'PR0010',
      productGroup: _getPRGSelectItemDataById('XXPRG0010', prgList),
      descr: 'CCaaS',
      type: {
        id: 1,
        label: 'Service',
      },
      level: {
        id: 2,
        label: 'Item',
      },
      keyIdentifierFieldName: '# Seats',
      numKitItems: 0,
      numSuppliers: 3,
      numOpportunities: 59,
      numOrders: 55,
      numServices: 55,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0013',
      hrid: 'PR0013',
      productGroup: _getPRGSelectItemDataById('XXPRG0008', prgList),
      descr: 'E-LAN (Kit)',
      type: {
        id: 1,
        label: 'Service',
      },
      level: {
        id: 1,
        label: 'Kit',
      },
      fieldSet: {
        id: 'XXFS0002',
        label: 'DIA',
      },
      keyIdentifierFieldName: '',
      numKitItems: 4,
      numSuppliers: 2,
      numOpportunities: 210,
      numOrders: 205,
      numServices: 240,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0016',
      hrid: 'PR0016',
      productGroup: _getPRGSelectItemDataById('XXPRG0008', prgList),
      descr: 'E-LAN Hub (Kit)',
      type: {
        id: 1,
        label: 'Service',
      },
      level: {
        id: 1,
        label: 'Kit',
      },
      keyIdentifierFieldName: '',
      numKitItems: 3,
      numSuppliers: 4,
      numOpportunities: 100,
      numOrders: 100,
      numServices: 160,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0017',
      hrid: 'PR0017',
      productGroup: _getPRGSelectItemDataById('XXPRG0008', prgList),
      descr: 'E-LAN Endpoint (Kit)',
      type: {
        id: 1,
        label: 'Service',
      },
      level: {
        id: 1,
        label: 'Kit',
      },
      keyIdentifierFieldName: '',
      numKitItems: 2,
      numSuppliers: 4,
      numOpportunities: 100,
      numOrders: 100,
      numServices: 2,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0018',
      hrid: 'PR0018',
      productGroup: _getPRGSelectItemDataById('XXPRG0008', prgList),
      descr: 'E-LAN Circuit',
      type: {
        id: 1,
        label: 'Service',
      },
      level: {
        id: 2,
        label: 'Item',
      },
      keyIdentifierFieldName: '',
      numKitItems: 0,
      numSuppliers: 4,
      numOpportunities: 100,
      numOrders: 100,
      numServices: 2,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0019',
      hrid: 'PR0019',
      productGroup: _getPRGSelectItemDataById('XXPRG0009', prgList),
      descr: 'SDWAN (Kit)',
      type: {
        id: 1,
        label: 'Service',
      },
      level: {
        id: 1,
        label: 'Kit',
      },
      keyIdentifierFieldName: '',
      // Consulting Services, License, and the SDWAN Device Set -- the four box
      // models moved into XXPR0064 so that "pick exactly one" is expressible.
      numKitItems: 3,
      numSuppliers: 1,
      numOpportunities: 80,
      numOrders: 78,
      numServices: 78,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0020',
      hrid: 'PR0020',
      productGroup: _getPRGSelectItemDataById('XXPRG0009', prgList),
      descr: 'V10Box',
      type: {
        id: 2,
        label: 'Hardware',
      },
      level: {
        id: 2,
        label: 'Item',
      },
      fieldSet: { id: 'XXFS0007', label: 'SDWAN Device' },
      keyIdentifierFieldName: '',
      numKitItems: 0,
      numSuppliers: 1,
      numOpportunities: 80,
      numOrders: 78,
      numServices: 78,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0021',
      hrid: 'PR0021',
      productGroup: _getPRGSelectItemDataById('XXPRG0009', prgList),
      descr: 'V120Box',
      type: {
        id: 2,
        label: 'Hardware',
      },
      level: {
        id: 2,
        label: 'Item',
      },
      fieldSet: { id: 'XXFS0007', label: 'SDWAN Device' },
      keyIdentifierFieldName: '',
      numKitItems: 0,
      numSuppliers: 1,
      numOpportunities: 80,
      numOrders: 78,
      numServices: 78,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0022',
      hrid: 'PR0022',
      productGroup: _getPRGSelectItemDataById('XXPRG0009', prgList),
      descr: 'V180Box',
      type: {
        id: 2,
        label: 'Hardware',
      },
      level: {
        id: 2,
        label: 'Item',
      },
      fieldSet: { id: 'XXFS0007', label: 'SDWAN Device' },
      keyIdentifierFieldName: '',
      numKitItems: 0,
      numSuppliers: 1,
      numOpportunities: 80,
      numOrders: 78,
      numServices: 78,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0023',
      hrid: 'PR0023',
      productGroup: _getPRGSelectItemDataById('XXPRG0009', prgList),
      descr: 'V1800Box',
      type: {
        id: 2,
        label: 'Hardware',
      },
      level: {
        id: 2,
        label: 'Item',
      },
      fieldSet: { id: 'XXFS0007', label: 'SDWAN Device' },
      keyIdentifierFieldName: '',
      numKitItems: 0,
      numSuppliers: 1,
      numOpportunities: 85,
      numOrders: 81,
      numServices: 81,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0025',
      hrid: 'PR0025',
      productGroup: _getPRGSelectItemDataById('XXPRG0006', prgList),
      descr: 'UCaaS (Kit)',
      type: { id: 1, label: 'Service' },
      level: { id: 1, label: 'Kit' },
      fieldSet: { id: 'XXFS0006', label: 'UCaaS' },
      status: { id: 2, label: 'Active' },
      createdAt: '2024-03-10T01:00:00.000Z',
      modifiedAt: '2024-03-10T01:00:00.000Z',
      version: '1',
      canBeDeleted: true,
      numKitItems: 3,
      numSuppliers: 1,
    },
    {
      id: 'XXPR0026',
      hrid: 'PR0026',
      productGroup: _getPRGSelectItemDataById('XXPRG0006', prgList),
      descr: 'Standard License',
      type: { id: 1, label: 'Service' },
      level: { id: 2, label: 'Item' },
      status: { id: 2, label: 'Active' },
      createdAt: '2024-03-10T01:02:00.000Z',
      modifiedAt: '2024-03-10T01:02:00.000Z',
      version: '1',
      canBeDeleted: true,
      numSuppliers: 1,
    },
    {
      id: 'XXPR0027',
      hrid: 'PR0027',
      productGroup: _getPRGSelectItemDataById('XXPRG0006', prgList),
      descr: 'e911 Service Fee',
      type: { id: 3, label: 'Fee' },
      level: { id: 2, label: 'Item' },
      status: { id: 2, label: 'Active' },
      createdAt: '2024-03-10T01:02:00.000Z',
      modifiedAt: '2024-03-10T01:02:00.000Z',
      version: '1',
      canBeDeleted: true,
      numSuppliers: 1,
    },
    {
      id: 'XXPR0028',
      hrid: 'PR0028',
      productGroup: _getPRGSelectItemDataById('XXPRG0006', prgList),
      descr: 'Compliance and Administrative Cost Recovery Fee',
      type: { id: 3, label: 'Fee' },
      level: { id: 2, label: 'Item' },
      status: { id: 2, label: 'Active' },
      createdAt: '2024-03-10T01:04:00.000Z',
      modifiedAt: '2024-03-10T01:04:00.000Z',
      version: '1',
      canBeDeleted: true,
      numSuppliers: 1,
    },
    {
      id: 'XXPR0029',
      hrid: 'PR0029',
      productGroup: _getPRGSelectItemDataById('XXPRG0006', prgList),
      descr: 'TAM - Silver',
      type: { id: 1, label: 'Service' },
      level: { id: 2, label: 'Item' },
      status: { id: 2, label: 'Active' },
      createdAt: '2024-03-10T01:38:00.000Z',
      modifiedAt: '2024-03-10T01:38:00.000Z',
      version: '1',
      canBeDeleted: true,
      numSuppliers: 1,
    },
    {
      id: 'XXPR0030',
      hrid: 'PR0030',
      productGroup: _getPRGSelectItemDataById('XXPRG0006', prgList),
      descr: 'TAM - Gold',
      type: { id: 1, label: 'Service' },
      level: { id: 2, label: 'Item' },
      status: { id: 2, label: 'Active' },
      createdAt: '2024-03-10T01:41:00.000Z',
      modifiedAt: '2024-03-10T01:41:00.000Z',
      version: '1',
      canBeDeleted: true,
      numSuppliers: 1,
    },
    {
      id: 'XXPR0031',
      hrid: 'PR0031',
      productGroup: _getPRGSelectItemDataById('XXPRG0006', prgList),
      descr: 'TAM - Bronze',
      type: { id: 1, label: 'Service' },
      level: { id: 2, label: 'Item' },
      status: { id: 2, label: 'Active' },
      createdAt: '2024-03-10T01:42:00.000Z',
      modifiedAt: '2024-03-10T01:42:00.000Z',
      version: '1',
      canBeDeleted: true,
      numSuppliers: 1,
    },
    {
      id: 'XXPR0032',
      hrid: 'PR0032',
      productGroup: _getPRGSelectItemDataById('XXPRG0006', prgList),
      descr: 'Technical Account Manager (Set)',
      type: { id: 1, label: 'Service' },
      level: { id: 1, label: 'Kit' },
      doExcludeKitServiceWhenBuildingServices: true,
      status: { id: 2, label: 'Active' },
      createdAt: '2024-03-10T01:43:00.000Z',
      modifiedAt: '2024-03-10T01:43:00.000Z',
      version: '1',
      canBeDeleted: true,
      numKitItems: 3,
      numSuppliers: 1,
    },
    {
      id: 'XXPR0033',
      hrid: 'PR0033',
      productGroup: _getPRGSelectItemDataById('XXPRG0005', prgList),
      descr: 'Toll Free Number',
      type: { id: 1, label: 'Service' },
      level: { id: 2, label: 'Item' },
      fieldSet: { id: 'XXFS0008', label: 'Toll Free Numbers' },
      status: { id: 2, label: 'Active' },
      createdAt: '2024-03-10T01:47:00.000Z',
      modifiedAt: '2024-03-10T01:47:00.000Z',
      version: '1',
      canBeDeleted: true,
      numSuppliers: 1,
    },
    {
      id: 'XXPR0034',
      hrid: 'PR0034',
      productGroup: _getPRGSelectItemDataById('XXPRG0006', prgList),
      descr: 'Digital Unlimited Line Advanced',
      type: { id: 1, label: 'Service' },
      level: { id: 2, label: 'Item' },
      status: { id: 2, label: 'Active' },
      createdAt: '2024-03-10T01:43:00.000Z',
      modifiedAt: '2024-03-10T01:43:00.000Z',
      version: '1',
      canBeDeleted: true,
      numSuppliers: 1,
    },
    {
      id: 'XXPR0035',
      hrid: 'PR0035',
      productGroup: _getPRGSelectItemDataById('XXPRG0002', prgList),
      descr: 'DIA (Kit)',
      type: {
        id: 1,
        label: 'Service',
      },
      level: {
        id: 1,
        label: 'Kit',
      },
      fieldSet: {
        id: 'XXFS0002',
        label: 'DIA',
      },
      orderDetailFieldSet: {
        id: 'XXFS0009',
        label: 'Circuit',
      },
      numDaysToLateAmber: 30,
      numDaysToLateRed: 60,
      keyIdentifierFieldName: 'circuitId',
      numKitItems: 4,
      numSuppliers: 4,
      numOpportunities: 182,
      numOrders: 160,
      numServices: 160,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0036',
      hrid: 'PR0036',
      productGroup: _getPRGSelectItemDataById('XXPRG0002', prgList),
      descr: 'DIA Bandwidth (Set)',
      type: {
        id: 1,
        label: 'Service',
      },
      level: {
        id: 1,
        label: 'Kit',
      },
      doExcludeKitServiceWhenBuildingServices: true,
      inKitOnly: true,
      keyIdentifierFieldName: '',
      numKitItems: 13,
      numSuppliers: 4,
      numOpportunities: 182,
      numOrders: 160,
      numServices: 160,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0037',
      hrid: 'PR0037',
      productGroup: _getPRGSelectItemDataById('XXPRG0002', prgList),
      descr: 'Port - 10Mb',
      type: {
        id: 1,
        label: 'Service',
      },
      level: {
        id: 2,
        label: 'Item',
      },
      // fieldSet: {
      //   id: 'XXFS0002',
      //   label: 'DIA',
      // },
      inKitOnly: true,
      keyIdentifierFieldName: '',
      numKitItems: 0,
      numSuppliers: 4,
      numOpportunities: 182,
      numOrders: 160,
      numServices: 160,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0038',
      hrid: 'PR0038',
      productGroup: _getPRGSelectItemDataById('XXPRG0002', prgList),
      descr: 'Port - 20Mb',
      type: {
        id: 1,
        label: 'Service',
      },
      level: {
        id: 2,
        label: 'Item',
      },
      // fieldSet: {
      //   id: 'XXFS0002',
      //   label: 'DIA',
      // },
      inKitOnly: true,
      keyIdentifierFieldName: '',
      numKitItems: 0,
      numSuppliers: 4,
      numOpportunities: 182,
      numOrders: 160,
      numServices: 160,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0039',
      hrid: 'PR0039',
      productGroup: _getPRGSelectItemDataById('XXPRG0002', prgList),
      descr: 'Port - 50Mb',
      type: {
        id: 1,
        label: 'Service',
      },
      level: {
        id: 2,
        label: 'Item',
      },
      // fieldSet: {
      //   id: 'XXFS0002',
      //   label: 'DIA',
      // },
      inKitOnly: true,
      keyIdentifierFieldName: '',
      numKitItems: 0,
      numSuppliers: 4,
      numOpportunities: 182,
      numOrders: 160,
      numServices: 160,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0040',
      hrid: 'PR0040',
      productGroup: _getPRGSelectItemDataById('XXPRG0002', prgList),
      descr: 'Port - 100Mb',
      type: {
        id: 1,
        label: 'Service',
      },
      level: {
        id: 2,
        label: 'Item',
      },
      // fieldSet: {
      //   id: 'XXFS0002',
      //   label: 'DIA',
      // },
      inKitOnly: true,
      keyIdentifierFieldName: '',
      numKitItems: 0,
      numSuppliers: 4,
      numOpportunities: 182,
      numOrders: 160,
      numServices: 160,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0042',
      hrid: 'PR0042',
      productGroup: _getPRGSelectItemDataById('XXPRG0002', prgList),
      descr: 'Port - 200Mb',
      type: {
        id: 1,
        label: 'Service',
      },
      level: {
        id: 2,
        label: 'Item',
      },
      // fieldSet: {
      //   id: 'XXFS0002',
      //   label: 'DIA',
      // },
      inKitOnly: true,
      keyIdentifierFieldName: '',
      numKitItems: 0,
      numSuppliers: 4,
      numOpportunities: 182,
      numOrders: 160,
      numServices: 160,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0043',
      hrid: 'PR0043',
      productGroup: _getPRGSelectItemDataById('XXPRG0002', prgList),
      descr: 'Port - 500Mb',
      type: {
        id: 1,
        label: 'Service',
      },
      level: {
        id: 2,
        label: 'Item',
      },
      // fieldSet: {
      //   id: 'XXFS0002',
      //   label: 'DIA',
      // },
      inKitOnly: true,
      keyIdentifierFieldName: '',
      numKitItems: 0,
      numSuppliers: 4,
      numOpportunities: 182,
      numOrders: 160,
      numServices: 160,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0044',
      hrid: 'PR0044',
      productGroup: _getPRGSelectItemDataById('XXPRG0002', prgList),
      descr: 'Port - 1Gb',
      type: {
        id: 1,
        label: 'Service',
      },
      level: {
        id: 2,
        label: 'Item',
      },
      // fieldSet: {
      //   id: 'XXFS0002',
      //   label: 'DIA',
      // },
      inKitOnly: true,
      keyIdentifierFieldName: '',
      numKitItems: 0,
      numSuppliers: 4,
      numOpportunities: 182,
      numOrders: 160,
      numServices: 160,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0045',
      hrid: 'PR0045',
      productGroup: _getPRGSelectItemDataById('XXPRG0002', prgList),
      descr: 'Port - 2Gb',
      type: {
        id: 1,
        label: 'Service',
      },
      level: {
        id: 2,
        label: 'Item',
      },
      // fieldSet: {
      //   id: 'XXFS0002',
      //   label: 'DIA',
      // },
      inKitOnly: true,
      keyIdentifierFieldName: '',
      numKitItems: 0,
      numSuppliers: 4,
      numOpportunities: 182,
      numOrders: 160,
      numServices: 160,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0046',
      hrid: 'PR0046',
      productGroup: _getPRGSelectItemDataById('XXPRG0002', prgList),
      descr: 'Port - 5Gb',
      type: {
        id: 1,
        label: 'Service',
      },
      level: {
        id: 2,
        label: 'Item',
      },
      // fieldSet: {
      //   id: 'XXFS0002',
      //   label: 'DIA',
      // },
      inKitOnly: true,
      keyIdentifierFieldName: '',
      numKitItems: 0,
      numSuppliers: 4,
      numOpportunities: 182,
      numOrders: 160,
      numServices: 160,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0047',
      hrid: 'PR0047',
      productGroup: _getPRGSelectItemDataById('XXPRG0002', prgList),
      descr: 'Port - 10Gb',
      type: {
        id: 1,
        label: 'Service',
      },
      level: {
        id: 2,
        label: 'Item',
      },
      // fieldSet: {
      //   id: 'XXFS0002',
      //   label: 'DIA',
      // },
      inKitOnly: true,
      keyIdentifierFieldName: '',
      numKitItems: 0,
      numSuppliers: 4,
      numOpportunities: 182,
      numOrders: 160,
      numServices: 160,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0048',
      hrid: 'PR0048',
      productGroup: _getPRGSelectItemDataById('XXPRG0002', prgList),
      descr: 'Port - 20Gb',
      type: {
        id: 1,
        label: 'Service',
      },
      level: {
        id: 2,
        label: 'Item',
      },
      // fieldSet: {
      //   id: 'XXFS0002',
      //   label: 'DIA',
      // },
      inKitOnly: true,
      keyIdentifierFieldName: '',
      numKitItems: 0,
      numSuppliers: 4,
      numOpportunities: 182,
      numOrders: 160,
      numServices: 160,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0049',
      hrid: 'PR0049',
      productGroup: _getPRGSelectItemDataById('XXPRG0002', prgList),
      descr: 'Port - 50Gb',
      type: {
        id: 1,
        label: 'Service',
      },
      level: {
        id: 2,
        label: 'Item',
      },
      // fieldSet: {
      //   id: 'XXFS0002',
      //   label: 'DIA',
      // },
      inKitOnly: true,
      keyIdentifierFieldName: '',
      numKitItems: 0,
      numSuppliers: 4,
      numOpportunities: 182,
      numOrders: 160,
      numServices: 160,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0050',
      hrid: 'PR0050',
      productGroup: _getPRGSelectItemDataById('XXPRG0002', prgList),
      descr: 'DIA Access (Set)',
      type: {
        id: 1,
        label: 'Service',
      },
      level: {
        id: 1,
        label: 'Kit',
      },
      doExcludeKitServiceWhenBuildingServices: true,
      inKitOnly: true,
      keyIdentifierFieldName: '',
      numKitItems: 3,
      numSuppliers: 4,
      numOpportunities: 182,
      numOrders: 160,
      numServices: 160,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0051',
      hrid: 'PR0051',
      productGroup: _getPRGSelectItemDataById('XXPRG0002', prgList),
      descr: 'Access - 1Gb',
      type: {
        id: 1,
        label: 'Service',
      },
      level: {
        id: 2,
        label: 'Item',
      },
      // fieldSet: {
      //   id: 'XXFS0002',
      //   label: 'DIA',
      // },
      inKitOnly: true,
      keyIdentifierFieldName: '',
      numKitItems: 0,
      numSuppliers: 4,
      numOpportunities: 182,
      numOrders: 160,
      numServices: 160,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0052',
      hrid: 'PR0052',
      productGroup: _getPRGSelectItemDataById('XXPRG0002', prgList),
      descr: 'Access - 10Gb',
      type: {
        id: 1,
        label: 'Service',
      },
      level: {
        id: 2,
        label: 'Item',
      },
      // fieldSet: {
      //   id: 'XXFS0002',
      //   label: 'DIA',
      // },
      inKitOnly: true,
      keyIdentifierFieldName: '',
      numKitItems: 0,
      numSuppliers: 4,
      numOpportunities: 182,
      numOrders: 160,
      numServices: 160,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0053',
      hrid: 'PR0053',
      productGroup: _getPRGSelectItemDataById('XXPRG0002', prgList),
      descr: 'Access - 100Gb',
      type: {
        id: 1,
        label: 'Service',
      },
      level: {
        id: 2,
        label: 'Item',
      },
      // fieldSet: {
      //   id: 'XXFS0002',
      //   label: 'DIA',
      // },
      inKitOnly: true,
      keyIdentifierFieldName: '',
      numKitItems: 0,
      numSuppliers: 4,
      numOpportunities: 182,
      numOrders: 160,
      numServices: 160,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },

    {
      id: 'XXPR0054',
      hrid: 'PR0054',
      productGroup: _getPRGSelectItemDataById('XXPRG0002', prgList),
      descr: 'IP Address (Set)',
      type: {
        id: 1,
        label: 'Service',
      },
      level: {
        id: 1,
        label: 'Kit',
      },
      doExcludeKitServiceWhenBuildingServices: true,
      // The one Set whose members COEXIST: a DIA circuit routinely takes a /31
      // for the point-to-point link AND a /29 for the LAN. Every other Set here
      // (TAM tier, bandwidth, access, CPE, SDWAN box) is genuinely pick-one, so
      // this is the only fixture that exercises the multi-select path.
      doAllowMultipleSelectionsFromSet: true,
      inKitOnly: true,
      keyIdentifierFieldName: '',
      numKitItems: 5,
      numSuppliers: 4,
      numOpportunities: 182,
      numOrders: 160,
      numServices: 160,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0055',
      hrid: 'PR0055',
      productGroup: _getPRGSelectItemDataById('XXPRG0002', prgList),
      descr: 'IP Block - /24',
      type: {
        id: 1,
        label: 'Service',
      },
      level: {
        id: 2,
        label: 'Item',
      },
      fieldSet: {
        id: 'XXFS0005',
        label: 'Network Base',
      },
      inKitOnly: true,
      keyIdentifierFieldName: 'circuitId',
      numKitItems: 0,
      numSuppliers: 4,
      numOpportunities: 182,
      numOrders: 160,
      numServices: 160,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0056',
      hrid: 'PR0056',
      productGroup: _getPRGSelectItemDataById('XXPRG0002', prgList),
      descr: 'IP Block - /29',
      type: {
        id: 1,
        label: 'Service',
      },
      level: {
        id: 2,
        label: 'Item',
      },
      fieldSet: {
        id: 'XXFS0005',
        label: 'Network Base',
      },
      inKitOnly: true,
      keyIdentifierFieldName: '',
      numKitItems: 0,
      numSuppliers: 4,
      numOpportunities: 182,
      numOrders: 160,
      numServices: 160,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0057',
      hrid: 'PR0057',
      productGroup: _getPRGSelectItemDataById('XXPRG0002', prgList),
      descr: 'IP Block - /30',
      type: {
        id: 1,
        label: 'Service',
      },
      level: {
        id: 2,
        label: 'Item',
      },
      fieldSet: {
        id: 'XXFS0005',
        label: 'Network Base',
      },
      inKitOnly: true,
      keyIdentifierFieldName: 'circuitId',
      numKitItems: 0,
      numSuppliers: 4,
      numOpportunities: 182,
      numOrders: 160,
      numServices: 160,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0058',
      hrid: 'PR0058',
      productGroup: _getPRGSelectItemDataById('XXPRG0002', prgList),
      descr: 'IP Block - /31',
      type: {
        id: 1,
        label: 'Service',
      },
      level: {
        id: 2,
        label: 'Item',
      },
      fieldSet: {
        id: 'XXFS0005',
        label: 'Network Base',
      },
      inKitOnly: true,
      keyIdentifierFieldName: '',
      numKitItems: 0,
      numSuppliers: 4,
      numOpportunities: 182,
      numOrders: 160,
      numServices: 160,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0059',
      hrid: 'PR0059',
      productGroup: _getPRGSelectItemDataById('XXPRG0002', prgList),
      descr: 'Customer Owned - BGP',
      type: {
        id: 1,
        label: 'Service',
      },
      level: {
        id: 2,
        label: 'Item',
      },
      // fieldSet: {
      //   id: 'XXFS0002',
      //   label: 'DIA',
      // },
      inKitOnly: true,
      keyIdentifierFieldName: '',
      numKitItems: 0,
      numSuppliers: 4,
      numOpportunities: 182,
      numOrders: 160,
      numServices: 160,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0060',
      hrid: 'PR0060',
      productGroup: _getPRGSelectItemDataById('XXPRG0002', prgList),
      descr: 'CPE (Set)',
      type: {
        id: 2,
        label: 'Hardware',
      },
      level: {
        id: 1,
        label: 'Kit',
      },
      doExcludeKitServiceWhenBuildingServices: true,
      inKitOnly: true,
      keyIdentifierFieldName: '',
      numKitItems: 5,
      numSuppliers: 4,
      numOpportunities: 182,
      numOrders: 160,
      numServices: 160,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0061',
      hrid: 'PR0061',
      productGroup: _getPRGSelectItemDataById('XXPRG0004', prgList),
      descr: 'Customer Provided',
      type: {
        id: 2,
        label: 'Hardware',
      },
      level: {
        id: 2,
        label: 'Item',
      },
      // fieldSet: {
      //   id: 'XXFS0002',
      //   label: 'DIA',
      // },
      keyIdentifierFieldName: '',
      numKitItems: 0,
      numSuppliers: 4,
      numOpportunities: 182,
      numOrders: 160,
      numServices: 160,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0062',
      hrid: 'PR0062',
      productGroup: _getPRGSelectItemDataById('XXPRG0004', prgList),
      descr: 'Fiber Converter',
      type: {
        id: 2,
        label: 'Hardware',
      },
      level: {
        id: 2,
        label: 'Item',
      },
      // fieldSet: {
      //   id: 'XXFS0002',
      //   label: 'DIA',
      // },
      keyIdentifierFieldName: '',
      numKitItems: 0,
      numSuppliers: 4,
      numOpportunities: 182,
      numOrders: 160,
      numServices: 160,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0063',
      hrid: 'PR0063',
      productGroup: _getPRGSelectItemDataById('XXPRG0004', prgList),
      descr: 'Carrier NID',
      type: {
        id: 2,
        label: 'Hardware',
      },
      level: {
        id: 2,
        label: 'Item',
      },
      // fieldSet: {
      //   id: 'XXFS0002',
      //   label: 'DIA',
      // },
      keyIdentifierFieldName: '',
      numKitItems: 0,
      numSuppliers: 4,
      numOpportunities: 182,
      numOrders: 160,
      numServices: 160,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      // The SDWAN kit's four box models, as a Set.
      //
      // They used to be four independent optional items ON the plain SDWAN kit,
      // which is legal but says the wrong thing: a deal takes exactly one box,
      // and only a Set (doExcludeKitServiceWhenBuildingServices) can express
      // that -- enforceOnePerSection / validateOnePerSection both gate on the
      // OWNING supplier-product being a Set. Modelled on XXPR0060 CPE (Set),
      // the other hardware Set.
      id: 'XXPR0064',
      hrid: 'PR0064',
      productGroup: _getPRGSelectItemDataById('XXPRG0009', prgList),
      descr: 'SDWAN Device (Set)',
      type: {
        id: 2,
        label: 'Hardware',
      },
      level: {
        id: 1,
        label: 'Kit',
      },
      doExcludeKitServiceWhenBuildingServices: true,
      inKitOnly: true,
      keyIdentifierFieldName: '',
      numKitItems: 4,
      numSuppliers: 1,
      numOpportunities: 80,
      numOrders: 78,
      numServices: 78,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      // A SET INSIDE A SET: one of CPE (Set)'s alternatives (XXPRKI0060) is
      // itself a Set of routers. Taking it means picking one router; taking
      // any other CPE alternative means no router at all. The only nested Set
      // in the mock data -- the fixture for the dependent section in the
      // options chooser (cynqueModules/optionSections.ts).
      //
      // Legal: a Set may hold Sets, just not plain Kits (the product editor's
      // filter in productKitItem.ts). Modelled on XXPR0064 SDWAN Device (Set).
      id: 'XXPR0065',
      hrid: 'PR0065',
      productGroup: _getPRGSelectItemDataById('XXPRG0002', prgList),
      descr: 'Managed Router (Set)',
      type: {
        id: 2,
        label: 'Hardware',
      },
      level: {
        id: 1,
        label: 'Kit',
      },
      doExcludeKitServiceWhenBuildingServices: true,
      inKitOnly: true,
      keyIdentifierFieldName: '',
      numKitItems: 2,
      numSuppliers: 1,
      numOpportunities: 0,
      numOrders: 0,
      numServices: 0,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0066',
      hrid: 'PR0066',
      productGroup: _getPRGSelectItemDataById('XXPRG0004', prgList),
      descr: 'Router - Cisco ISR',
      type: {
        id: 2,
        label: 'Hardware',
      },
      level: {
        id: 2,
        label: 'Item',
      },
      keyIdentifierFieldName: '',
      numKitItems: 0,
      numSuppliers: 1,
      numOpportunities: 0,
      numOrders: 0,
      numServices: 0,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0067',
      hrid: 'PR0067',
      productGroup: _getPRGSelectItemDataById('XXPRG0004', prgList),
      descr: 'Router - Juniper SRX',
      type: {
        id: 2,
        label: 'Hardware',
      },
      level: {
        id: 2,
        label: 'Item',
      },
      keyIdentifierFieldName: '',
      numKitItems: 0,
      numSuppliers: 1,
      numOpportunities: 0,
      numOrders: 0,
      numServices: 0,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    /**
     * BRANCH OFFICE -- the fixture's OPTIONAL NESTED PLAIN KIT.
     *
     *   Branch Office (Kit)            XXPR0068
     *     Router - Cisco ISR           mandatory
     *     Wi-Fi (Kit)                  XXPR0069  OPTIONAL -- an add-on Kit
     *       Wi-Fi Access Point         mandatory, x2
     *       Wi-Fi Controller License   optional
     *       Wi-Fi Mounting (Kit)       XXPR0070  mandatory, nested in the option
     *         Mounting Bracket         mandatory
     *
     * A Set can be optional and has no row; a plain Kit has a header row, so
     * taking or leaving out the Wi-Fi Kit builds or supersedes that header
     * WITH everything beneath it -- including the Mounting Kit's own header.
     * Every other nested kit in the mock data (the E-LAN Hubs and Endpoints)
     * is mandatory, so none of that had anything to run against. Supplier-side
     * in XXSP0025-XXSP0027; rows in XXOMS0012.
     */
    {
      id: 'XXPR0068',
      hrid: 'PR0068',
      productGroup: _getPRGSelectItemDataById('XXPRG0004', prgList),
      descr: 'Branch Office (Kit)',
      type: {
        id: 1,
        label: 'Service',
      },
      level: {
        id: 1,
        label: 'Kit',
      },
      keyIdentifierFieldName: '',
      numKitItems: 2,
      numSuppliers: 1,
      numOpportunities: 0,
      numOrders: 0,
      numServices: 0,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0069',
      hrid: 'PR0069',
      productGroup: _getPRGSelectItemDataById('XXPRG0004', prgList),
      descr: 'Wi-Fi (Kit)',
      type: {
        id: 1,
        label: 'Service',
      },
      level: {
        id: 1,
        label: 'Kit',
      },
      keyIdentifierFieldName: '',
      numKitItems: 3,
      numSuppliers: 1,
      numOpportunities: 0,
      numOrders: 0,
      numServices: 0,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0070',
      hrid: 'PR0070',
      productGroup: _getPRGSelectItemDataById('XXPRG0004', prgList),
      descr: 'Wi-Fi Mounting (Kit)',
      type: {
        id: 2,
        label: 'Hardware',
      },
      level: {
        id: 1,
        label: 'Kit',
      },
      keyIdentifierFieldName: '',
      numKitItems: 1,
      numSuppliers: 1,
      numOpportunities: 0,
      numOrders: 0,
      numServices: 0,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0071',
      hrid: 'PR0071',
      productGroup: _getPRGSelectItemDataById('XXPRG0004', prgList),
      descr: 'Wi-Fi Access Point',
      type: {
        id: 2,
        label: 'Hardware',
      },
      level: {
        id: 2,
        label: 'Item',
      },
      keyIdentifierFieldName: '',
      inKitOnly: true,
      numKitItems: 0,
      numSuppliers: 1,
      numOpportunities: 0,
      numOrders: 0,
      numServices: 0,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0072',
      hrid: 'PR0072',
      productGroup: _getPRGSelectItemDataById('XXPRG0004', prgList),
      descr: 'Wi-Fi Controller License',
      type: {
        id: 1,
        label: 'Service',
      },
      level: {
        id: 2,
        label: 'Item',
      },
      keyIdentifierFieldName: '',
      inKitOnly: true,
      numKitItems: 0,
      numSuppliers: 1,
      numOpportunities: 0,
      numOrders: 0,
      numServices: 0,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0073',
      hrid: 'PR0073',
      productGroup: _getPRGSelectItemDataById('XXPRG0004', prgList),
      descr: 'Mounting Bracket',
      type: {
        id: 2,
        label: 'Hardware',
      },
      level: {
        id: 2,
        label: 'Item',
      },
      keyIdentifierFieldName: '',
      inKitOnly: true,
      numKitItems: 0,
      numSuppliers: 1,
      numOpportunities: 0,
      numOrders: 0,
      numServices: 0,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    /**
     * ZSCALER DLP - 3 YEAR -- three year-kits nested in one Kit, modelled on a
     * production opportunity's "Zscaler DLP (1 of 3)" .. "(3 of 3)", whose
     * items all recur ANNUALLY and whose terms are 12, 12 (one Deployment
     * removed at term 1) and 5. Production holds the three as top-level kits;
     * here they are nested so the experiment can run against one Kit.
     *
     *   Zscaler DLP - 3 Year (Kit)               XXPR0074
     *     Data Loss Protection (DLP) (Kit)       XXPR0075  mandatory, x3 slots
     *       DLP Licensing (Set)                  XXPR0076  mandatory
     *         Zscaler                            XXPR0078  optional
     *       SASE Support & Implementation (Set)  XXPR0077  mandatory
     *         Zscaler                            XXPR0078  optional
     *
     * Both Sets mirror production: no selection allowed at product level, and
     * multi-select at supplier level, so their options coexist. Every item is
     * the ONE product "Zscaler"; the supplier-product items tell them apart.
     * Supplier-side in XXSP0028-XXSP0031; rows in XXOMS0013 on XXO0007.
     */
    {
      id: 'XXPR0074',
      hrid: 'PR0074',
      productGroup: _getPRGSelectItemDataById('XXPRG0001', prgList),
      descr: 'Zscaler DLP - 3 Year (Kit)',
      type: {
        id: 1,
        label: 'Service',
      },
      level: {
        id: 1,
        label: 'Kit',
      },
      keyIdentifierFieldName: '',
      numKitItems: 3,
      numSuppliers: 1,
      numOpportunities: 0,
      numOrders: 0,
      numServices: 0,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0075',
      hrid: 'PR0075',
      productGroup: _getPRGSelectItemDataById('XXPRG0001', prgList),
      descr: 'Data Loss Protection (DLP) (Kit)',
      type: {
        id: 1,
        label: 'Service',
      },
      level: {
        id: 1,
        label: 'Kit',
      },
      keyIdentifierFieldName: '',
      numKitItems: 2,
      numSuppliers: 1,
      numOpportunities: 0,
      numOrders: 0,
      numServices: 0,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0076',
      hrid: 'PR0076',
      productGroup: _getPRGSelectItemDataById('XXPRG0001', prgList),
      descr: 'DLP Licensing (Set)',
      type: {
        id: 1,
        label: 'Service',
      },
      level: {
        id: 1,
        label: 'Kit',
      },
      doExcludeKitServiceWhenBuildingServices: true,
      doAllowNoSelectionFromSet: true,
      inKitOnly: true,
      keyIdentifierFieldName: '',
      numKitItems: 1,
      numSuppliers: 1,
      numOpportunities: 0,
      numOrders: 0,
      numServices: 0,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0077',
      hrid: 'PR0077',
      productGroup: _getPRGSelectItemDataById('XXPRG0001', prgList),
      descr: 'SASE Support & Implementation (Set)',
      type: {
        id: 1,
        label: 'Service',
      },
      level: {
        id: 1,
        label: 'Kit',
      },
      doExcludeKitServiceWhenBuildingServices: true,
      doAllowNoSelectionFromSet: true,
      inKitOnly: true,
      keyIdentifierFieldName: '',
      numKitItems: 1,
      numSuppliers: 1,
      numOpportunities: 0,
      numOrders: 0,
      numServices: 0,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPR0078',
      hrid: 'PR0078',
      productGroup: _getPRGSelectItemDataById('XXPRG0001', prgList),
      descr: 'Zscaler',
      type: {
        id: 1,
        label: 'Service',
      },
      level: {
        id: 2,
        label: 'Item',
      },
      inKitOnly: true,
      keyIdentifierFieldName: '',
      numKitItems: 0,
      numSuppliers: 1,
      numOpportunities: 0,
      numOrders: 0,
      numServices: 0,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
  ]
}
